import React, { useState, useEffect, useRef } from 'react';
import { FaSpinner } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import TypingMessage from './TypingMessage';
import './Chatbot.css';
import HeaderChat from '../../components/HeaderChat';
import { useRateLimit } from '../../hooks/useRateLimit';

const API_KEY = process.env.REACT_APP_DEEPSEEK_API_KEY;
const BASE_URL = 'https://api.deepseek.com/chat/completions';

const SYSTEM_INSTRUCTION = `Você é um assistente de reparo automotivo que ajuda mecânicos e técnicos profissionais.
Forneça respostas concisas e objetivas, focadas em soluções práticas.
Atue como um parceiro de trabalho, não como um professor — dê respostas diretas, sem explicações desnecessárias, a menos que isso seja solicitado especificamente.
Concentre-se em: códigos DTC, diagnósticos, lubrificantes, funcionamento de sistemas, informações sobre peças e procedimentos comuns de reparo.
Mantenha as respostas curtas e diretas. Só forneça explicações detalhadas quando o usuário solicitar explicitamente mais informações.

DIRETRIZES DE FORMATAÇÃO MARKDOWN:
- Use **negrito** para termos técnicos importantes, nomes de peças e especificações
- Use \`código inline\` para códigos DTC (ex: \`P0300\`, \`P0171\`), valores técnicos e medições
- Use blocos de código para múltiplos códigos ou especificações técnicas
- Use ## para seções principais (ex: ## Diagnóstico, ## Solução, ## Causas)
- Use ### para subseções (ex: ### Causas Possíveis, ### Procedimento)
- Use listas com marcadores para etapas, causas ou múltiplos itens
- Use listas numeradas para procedimentos sequenciais
- Use > para avisos ou notas importantes
- Use tabelas para especificações ao comparar múltiplos valores`;

const Chatbot = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [displayedMessage, setDisplayedMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);
    const abortControllerRef = useRef(null);
    const { isLimited, errorMessage, checkAndIncrement } = useRateLimit();

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, displayedMessage]);

    const handleSend = async () => {
        if (input.trim() === '' || loading || isLimited) return;

        const allowed = await checkAndIncrement();
        if (!allowed) return;

        const userMessage = { role: 'user', content: input };
        setMessages((prevMessages) => [...prevMessages, userMessage]);
        setInput('');
        setLoading(true);
        setIsTyping(true);

        try {
            const botMessage = { role: 'assistant', content: '' };
            setMessages((prevMessages) => [...prevMessages, botMessage]);

            const history = messages.map((msg) => ({
                role: msg.role === 'model' ? 'assistant' : msg.role,
                content: msg.content,
            }));

            const apiMessages = [
                { role: 'system', content: SYSTEM_INSTRUCTION },
                ...history,
                { role: 'user', content: input },
            ];

            abortControllerRef.current = new AbortController();

            const response = await fetch(BASE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${API_KEY}`,
                },
                body: JSON.stringify({
                    model: 'deepseek-v4-flash',
                    messages: apiMessages,
                    max_tokens: 3600,
                    temperature: 0.4,
                    top_p: 0.8,
                    stream: true,
                }),
                signal: abortControllerRef.current.signal,
            });

            if (!response.ok) {
                throw new Error('No momento não foi possível concluir sua solicitação. Tente novamente em instantes.');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let fullResponse = '';
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(6);
                        if (data === '[DONE]') continue;
                        try {
                            const parsed = JSON.parse(data);
                            const content = parsed.choices?.[0]?.delta?.content || '';
                            fullResponse += content;

                            setMessages((prevMessages) => {
                                const newMessages = [...prevMessages];
                                newMessages[newMessages.length - 1].content = fullResponse;
                                return newMessages;
                            });

                            if (messagesEndRef.current) {
                                messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                            }
                        } catch (e) {
                            // Skip malformed JSON chunks
                        }
                    }
                }
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error("Erro na solicitação:", error);
                setMessages((prevMessages) => {
                    const newMessages = [...prevMessages];
                    newMessages[newMessages.length - 1].content =
                        'No momento não foi possível concluir sua solicitação. Tente novamente em instantes.';
                    return newMessages;
                });
            }
        } finally {
            setLoading(false);
            setIsTyping(false);
            abortControllerRef.current = null;
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSend();
        }
    };

    return (
        <div className="chatbot-container">
            {errorMessage && (
                <div className="rate-limit-error">{errorMessage}</div>
            )}
            <div className="messages">
                {messages.map((message, index) => (
                    <div key={index} className={`message ${message.role === 'user' ? 'user' : 'bot'}`}>
                        <img
                            src={message.role === 'user' ? '/images/user-avatar.png' : '/images/bot-avatar.png'}
                            alt={`${message.role} avatar`}
                            className="avatar"
                        />
                        {message.role === 'assistant' ? (
                            <ReactMarkdown className="message-content">
                                {message.content}
                            </ReactMarkdown>
                        ) : (
                            <span className="message-content">{message.content}</span>
                        )}
                    </div>
                ))}

                {loading && (
                    <div className="message bot typing">
                        <div className="message-content">
                            <TypingMessage content="" />
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>
            <div className="input-container">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={isLimited ? 'Limite de perguntas atingido' : 'Digite sua mensagem...'}
                    disabled={isLimited}
                />
                <button onClick={handleSend} disabled={loading || isLimited}>
                    {loading ? <FaSpinner className="spinner" /> : 'Enviar'}
                </button>
            </div>
        </div>
    );
};

export default Chatbot;
